import React, { FC, ReactElement, ReactNode, } from "react";

import { Modal, } from "components/FormControls";
import {
    PoolRollOutcome,
    PoolRollResult,
    SingleRollResult,
    RollType,
    getPoolOutcome,
    retrieveRollHistory,
} from ".";

import "./style.scss";

type Props = {
    onCancel: () => void;
};

const LOG_ITEM_CLASSNAME = "__rollhistorylistitem__";

export const HistoryModal:FC<Props> = (props: Props): ReactElement => {
    const { onCancel, } = props;

    const entries = retrieveRollHistory();

    // TODO: support filters
    const content = renderContent(entries);

    return (
        <Modal
            title="Roll History"
            onCancel={onCancel}
            className="__rollhistorymodal__"
            bodyClassName="__rollhistorybody__"
        >
            {content}
        </Modal>
    );
};

const renderContent = (entries: (PoolRollResult | SingleRollResult)[] | undefined): ReactElement => {
    if (!entries) {
        return (
            <div className="__rollhistoryerror__">
                <span><strong className="text-danger">Error.</strong> Roll history not found.</span>
            </div>
        );
    }

    // we want to reverse the order, since the last item is the most recent
    entries.reverse();

    return (
        <ul className="__rollhistorylist__">
            {renderEntries(entries)}
        </ul>
    );
};

const renderEntries = (entries: (PoolRollResult | SingleRollResult)[]): ReactElement[] => {
    return entries.map((item, index) => {
        const type = item.type;
        return type === RollType.Pool
            ? renderPoolRollEntry(item as PoolRollResult, index)
            : renderSingleRollEntry(item as SingleRollResult, index);
    });
};

const renderTimestamp = (timestamp: number): ReactElement => {
    const datetime = new Date(timestamp);

    // date
    const fullyear = datetime.getFullYear();
    const month = `${datetime.getMonth() + 1}`.padStart(2, "0");
    const day = `${datetime.getDate()}`.padStart(2, "0");

    const date = <span className="__date__" key={1}>{fullyear}.{month}.{day}</span>;

    // time
    const hours = `${datetime.getHours()}`.padStart(2, "0");
    const minutes = `${datetime.getMinutes()}`.padStart(2, "0");
    const seconds = `${datetime.getSeconds()}`.padStart(2, "0");

    const time = <span className="__time__" key={2}>{hours}:{minutes}:{seconds}</span>;

    return <span className="__timestamp__" key={timestamp}>[{date} {time}]</span>;
};

const renderSingleRollEntry = (result: SingleRollResult, index: number): ReactElement => {
    const { type, roll, timestamp, } = result;

    // initialize content & create helper function
    const content = [] as (ReactElement | string)[];
    const addContent = (element: ReactElement) => {
        // add a space between elements
        content.push(" ");
        content.push(element);
    };

    // add heading
    const time = renderTimestamp(timestamp);
    const title = (type === RollType.Rouse) ? "Rouse" : "D10";
    addContent(<h6 className="__header__" key={0}>{time} {title}</h6>);

    // add roll result
    addContent(<span key={1}>Rolled <strong>{roll}</strong>.</span>);

    // add outcome if applicable
    if (type === RollType.Rouse) {
        if (roll < 6) {
            // failure
            addContent(<strong className="text-danger" key={2}>Failure.</strong>);
        } else {
            // success
            addContent(<strong className="text-success" key={3}>Success.</strong>);
        }
    }

    return (
        <li className={LOG_ITEM_CLASSNAME} key={index}>
            <div className="__singlerollentry__">{content}</div>
        </li>
    );
};


const renderPoolRollEntry = (result: PoolRollResult, index: number): ReactElement => {
    const { timestamp, pool, hunger, difficulty, } = result;

    // add heading
    const time = renderTimestamp(timestamp);
    const title = result.reroll ? "Pool Reroll" : "Pool";
    const heading = <h6 className="__header__" key={0}>{time} {title}</h6>;

    // initialize content & create helper function
    const content = [] as (ReactElement | string)[];
    const addContent = (element: ReactElement, addSpace: boolean = true) => {
        // optionally add a space between elements
        addSpace && content.push(" ");
        content.push(element);
    };

    // add pool roll, if applicable
    if (pool?.length) {
        const poolRolls = renderPoolDiceRolls(pool, false);
        addContent(<span key={1}><strong>Pool:</strong> Rolled {poolRolls}.</span>);
    }

    // add hunger roll, if applicable
    if (hunger !== undefined) {
        const hungerRolls = renderPoolDiceRolls(hunger, true);
        addContent(<span key={2}><strong>Hunger:</strong> Rolled {hungerRolls}.</span>);
    }


    const outcome = getPoolOutcome(result);
    let outcomeContent = <></>;

    // add difficulty, if applicable
    if (difficulty !== undefined) {
        addContent(<span key={3}><strong>Difficulty:</strong> {difficulty}.</span>);
        const definiteOutcome = renderPoolDefiniteOutcome(outcome);
        outcomeContent = <div className="content">{definiteOutcome}</div>;
    } else {
        // if there's no difficulty, we can immediately add the possible outcome text
        const possibleOutcome = renderPoolPossibleOutcome(outcome);
        outcomeContent = <div className="content">{possibleOutcome}</div>;
    }

    return (
        <li className={LOG_ITEM_CLASSNAME} key={index}>
            <div className="__poolrollentry__">
                {heading}
                <div className="content">{content}</div>
                {outcomeContent}
            </div>
        </li>
    );
};

// pool result utilities
// these are roughly the same as the ones used in Results.tsx,
// but with enough differences that they need to be discrete
const renderPoolDiceRolls = (rolls: number[] | undefined, isHunger?: boolean): ReactNode => {
    // no dice rolled
    if (!rolls || !rolls.length) {
        return (<span>&mdash;</span>);
    }

    // wrap the results in spans
    const tags = rolls.map<ReactNode>((num, index) => {
        const css = num === 10 ? "text-success" : (num === 1 && isHunger) ? "text-danger" : undefined;
        return (
            <strong key={index} className={css}>{num}</strong>
        );
    });

    // build the natural-language list
    const list = tags.reduce((prev, curr, index) => {
        if (tags.length === 2) {
            return [prev, " and ", curr];
        }

        if (index === tags.length - 1) {
            return [prev, ", and ", curr];
        }

        return [prev, ", ", curr];
    });

    return list;
};

const renderPoolPossibleOutcome = (outcome: PoolRollOutcome): ReactNode => {
    const { successes, poolCrits, hungerCrits, hungerFailures, } = outcome;

    // generate the success text; we always render this
    const successText = (
        <span key={"successes"}>
            <strong>{`${successes}`}</strong> {`${successes === 1 ? "success" : "successes"}`}.
        </span>
    );

    // create a store for the content we'll output
    const content = [successText] as (ReactElement | string)[];
    const addContent = (element: ReactElement) => {
        // add a space between elements
        content.push(" ");
        content.push(element);
    };

    if (poolCrits + (hungerCrits || 0) > 1) {
        // we have crits...
        const poolCrit = <strong className="text-success" key={"crit"}>Possible critical success.</strong>;
        const hungerCrit = <strong className="text-messy" key={"messy"}>Possible messy critical.</strong>;

        if (!hungerCrits) {
            // ... but no hunger crits
            // possibleCrit = poolCrit;
            // content.push(poolCrit);
            addContent(poolCrit);
        } else {
            // we have hunger crits
            if ((hungerCrits > 1) || (poolCrits % 2 !== 0)) {
                // at least a pair of crits: 2+ hunger crits; or 1+ hunger crit and 1+ pool crit
                addContent(hungerCrit);
            } else {
                addContent(poolCrit);
            }
        }
    }

    if (hungerFailures) {
        // bestial failure
        const possibleFailure = <strong className="text-danger" key={"bestial"}>Possible bestial failure.</strong>;
        addContent(possibleFailure);
    }

    return content;
};

const renderPoolDefiniteOutcome = (outcome: PoolRollOutcome): ReactNode => {
    const { successes, difficulty, hungerCrits, poolCrits, hungerFailures, } = outcome;

    // create a store for the content we'll output
    const content = [] as (ReactElement | string)[];
    const addContent = (element: ReactElement) => {
        // add a space between elements
        content.push(" ");
        content.push(element);
    };

    // did we succeed?
    if (successes >= (difficulty || 0)) {
        // success!
        if (((hungerCrits || 0) > 1) || (hungerCrits && ((poolCrits + hungerCrits) % 2 === 0))) {
            addContent(<span className="text-messy" key={1}><strong>Messy critical.</strong></span>);
        } else {
            if (poolCrits > 1) {
                // critical
                addContent(<span className="text-success" key={1}><strong>Critical success.</strong></span>);
            } else {
                // regular
                addContent(<span className="text-success" key={1}><strong>Success.</strong></span>);
            }
        }
    } else {
        // failure
        if (hungerFailures) {
            // bestial
            addContent(<span className="text-danger" key={1}><strong>Bestial failure.</strong></span>);
        } else {
            // regular
            addContent(<span className="text-danger" key={1}><strong>Failure.</strong></span>);
        }
    }

    addContent(<span key={2}><strong>{`${successes}`}</strong> {`${successes === 1 ? "success" : "successes"}`},</span>);
    addContent(<span key={3}><strong>{Math.abs(successes - (difficulty || 0))}</strong>-degree margin.</span>);

    return content;
}